import { baseApi } from './base';
import type { UpdateForwardPayload } from '@/types/recurrence';
import type {
  Lawsuit,
  CreateLawsuitRequest,
  UpdateLawsuitRequest,
  LawsuitSearchRequest,
  LawsuitSearchResponse,
  LawsuitParty,
  CreateLawsuitPartyRequest,
  UpdateLawsuitPartyRequest,
  LawsuitTimeline,
  CreateLawsuitTimelineRequest,
  UpdateLawsuitTimelineRequest,
  LawsuitDocument,
  CreateLawsuitDocumentRequest,
  UpdateLawsuitDocumentRequest,
  DocumentFolder,
  CreateDocumentFolderRequest,
  UpdateDocumentFolderRequest,
  LawsuitPayment,
  CreateLawsuitPaymentRequest,
  UpdateLawsuitPaymentRequest,
} from '@/types/lawsuit';

export const lawsuitService = {
  // Lawsuit Endpoints
  async getLawsuits(params: { limit: number; offset: number; ordering: string; search?: string; tags?: string; classification?: string; }): Promise<{ results: Lawsuit[], count: number }> {
    const queryParams: Record<string, string> = {
      limit: params.limit.toString(),
      offset: params.offset.toString(),
      ordering: params.ordering,
    };

    if (params.search) {
      queryParams.search = params.search;
    }

    if (params.tags) {
      queryParams.tags = params.tags;
    }

    if (params.classification) {
      queryParams.classification = params.classification;
    }

    const query = new URLSearchParams(queryParams).toString();
    return baseApi.request<{ results: Lawsuit[], count: number }>(`/lawsuits/?${query}`);
  },

  async getLawsuit(id: string): Promise<Lawsuit> {
    return baseApi.request<Lawsuit>(`/lawsuits/${id}/`);
  },

  async createLawsuit(data: CreateLawsuitRequest): Promise<Lawsuit> {
    return baseApi.request<Lawsuit>('/lawsuits/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateLawsuit(id: string, data: UpdateLawsuitRequest): Promise<Lawsuit> {
    return baseApi.request<Lawsuit>(`/lawsuits/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteLawsuit(id: string): Promise<void> {
    return baseApi.request<void>(`/lawsuits/${id}/`, {
      method: 'DELETE',
    });
  },

  async searchLawsuits(data: LawsuitSearchRequest): Promise<LawsuitSearchResponse> {
    return baseApi.request<LawsuitSearchResponse>(
      '/lawsuits/search/',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  async syncMyProcesses(): Promise<LawsuitSearchResponse> {
    return baseApi.request<LawsuitSearchResponse>(
      '/lawsuits/sync_my_processes/',
      {
        method: 'POST',
      }
    );
  },

  async getTaskStatus(taskId: string): Promise<{ task_id: string; state: string; result?: unknown; error?: string }> {
    return baseApi.request<{ task_id: string; state: string; result?: unknown; error?: string }>(`/lawsuits/tasks/${taskId}/`);
  },

  // LawsuitParty Endpoints
  async getLawsuitParties(lawsuitId: string): Promise<{ results: LawsuitParty[], count: number }> {
    return baseApi.request<{ results: LawsuitParty[], count: number }>(`/lawsuit-parties/?lawsuit=${lawsuitId}`);
  },

  async getLawsuitParty(id: string): Promise<LawsuitParty> {
    return baseApi.request<LawsuitParty>(`/lawsuit-parties/${id}/`);
  },

  async createLawsuitParty(data: CreateLawsuitPartyRequest): Promise<LawsuitParty> {
    return baseApi.request<LawsuitParty>('/lawsuit-parties/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateLawsuitParty(id: string, data: UpdateLawsuitPartyRequest): Promise<LawsuitParty> {
    return baseApi.request<LawsuitParty>(`/lawsuit-parties/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteLawsuitParty(id: string): Promise<void> {
    return baseApi.request<void>(`/lawsuit-parties/${id}/`, {
      method: 'DELETE',
    });
  },

  // LawsuitTimeline Endpoints
  async getLawsuitTimelines(lawsuitId: string): Promise<{ results: LawsuitTimeline[], count: number }> {
    return baseApi.request<{ results: LawsuitTimeline[], count: number }>(`/lawsuit-timelines/?lawsuit=${lawsuitId}`);
  },

  async getLawsuitTimeline(id: string): Promise<LawsuitTimeline> {
    return baseApi.request<LawsuitTimeline>(`/lawsuit-timelines/${id}/`);
  },

  async createLawsuitTimeline(data: CreateLawsuitTimelineRequest): Promise<LawsuitTimeline> {
    return baseApi.request<LawsuitTimeline>('/lawsuit-timelines/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateLawsuitTimeline(id: string, data: UpdateLawsuitTimelineRequest): Promise<LawsuitTimeline> {
    return baseApi.request<LawsuitTimeline>(`/lawsuit-timelines/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteLawsuitTimeline(id: string): Promise<void> {
    return baseApi.request<void>(`/lawsuit-timelines/${id}/`, {
      method: 'DELETE',
    });
  },

  async getLawsuitDocuments(lawsuitId: string, params: { folder?: string | null; folder__isnull?: boolean; folder__tags?: string } = {}): Promise<{ results: LawsuitDocument[], count: number }> {
    const queryParams: Record<string, string> = { lawsuit: lawsuitId };
    if (params.folder !== undefined && params.folder !== null) queryParams.folder = params.folder;
    if (params.folder__isnull !== undefined) queryParams.folder__isnull = params.folder__isnull.toString();
    if (params.folder__tags) queryParams.folder__tags = params.folder__tags;
    
    const query = new URLSearchParams(queryParams).toString();
    return baseApi.request<{ results: LawsuitDocument[], count: number }>(`/lawsuit-documents/?${query}`);
  },

  async getLawsuitDocument(id: string): Promise<LawsuitDocument> {
    return baseApi.request<LawsuitDocument>(`/lawsuit-documents/${id}/`);
  },

  async createLawsuitDocument(data: CreateLawsuitDocumentRequest): Promise<LawsuitDocument> {
    const formData = new FormData();
    formData.append('lawsuit', data.lawsuit);
    formData.append('type', data.type);
    formData.append('title', data.title);
    formData.append('file', data.file);
    if (data.folder) formData.append('folder', data.folder);

    return baseApi.request<LawsuitDocument>('/lawsuit-documents/', {
      method: 'POST',
      body: formData,
    });
  },

  async updateLawsuitDocument(id: string, data: UpdateLawsuitDocumentRequest): Promise<LawsuitDocument> {
    const formData = new FormData();
    if (data.lawsuit) formData.append('lawsuit', data.lawsuit);
    if (data.type) formData.append('type', data.type);
    if (data.title) formData.append('title', data.title);
    if (data.file) formData.append('file', data.file);
    if (data.folder !== undefined) formData.append('folder', data.folder || '');

    return baseApi.request<LawsuitDocument>(`/lawsuit-documents/${id}/`, {
      method: 'PATCH',
      body: formData,
    });
  },

  async deleteLawsuitDocument(id: string): Promise<void> {
    return baseApi.request<void>(`/lawsuit-documents/${id}/`, {
      method: 'DELETE',
    });
  },

  // DocumentFolder Endpoints
  async getDocumentFolders(params: { lawsuit?: string; tags?: string } = {}): Promise<{ results: DocumentFolder[], count: number }> {
    const queryParams: Record<string, string> = {};
    if (params.lawsuit) queryParams.lawsuit = params.lawsuit;
    if (params.tags) queryParams.tags = params.tags;

    const query = new URLSearchParams(queryParams).toString();
    return baseApi.request<{ results: DocumentFolder[], count: number }>(`/document-folders/?${query}`);
  },

  async getDocumentFolder(id: string): Promise<DocumentFolder> {
    return baseApi.request<DocumentFolder>(`/document-folders/${id}/`);
  },

  async createDocumentFolder(data: CreateDocumentFolderRequest): Promise<DocumentFolder> {
    return baseApi.request<DocumentFolder>('/document-folders/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateDocumentFolder(id: string, data: UpdateDocumentFolderRequest): Promise<DocumentFolder> {
    return baseApi.request<DocumentFolder>(`/document-folders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteDocumentFolder(id: string): Promise<void> {
    return baseApi.request<void>(`/document-folders/${id}/`, {
      method: 'DELETE',
    });
  },

  // LawsuitPayment Endpoints (within Lawsuit context)
  async getLawsuitPayments(lawsuitId: string, params: { limit?: number; offset?: number; type?: string; paid?: boolean } = {}): Promise<{ results: LawsuitPayment[], count: number }> {
    const queryParams: Record<string, string> = { lawsuit: lawsuitId };
    if (params.limit !== undefined) queryParams.limit = params.limit.toString();
    if (params.offset !== undefined) queryParams.offset = params.offset.toString();
    if (params.type) queryParams.type = params.type;
    if (params.paid !== undefined) queryParams.paid = params.paid.toString();

    const query = new URLSearchParams(queryParams).toString();
    return baseApi.request<{ results: LawsuitPayment[], count: number }>(`/lawsuit-payments/?${query}`);
  },

  async getLawsuitPayment(id: string): Promise<LawsuitPayment> {
    return baseApi.request<LawsuitPayment>(`/lawsuit-payments/${id}/`);
  },

  async createLawsuitPayment(data: CreateLawsuitPaymentRequest): Promise<LawsuitPayment> {
    return baseApi.request<LawsuitPayment>('/lawsuit-payments/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateLawsuitPayment(id: string, data: UpdateLawsuitPaymentRequest): Promise<LawsuitPayment> {
    return baseApi.request<LawsuitPayment>(`/lawsuit-payments/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteLawsuitPayment(id: string): Promise<void> {
    return baseApi.request<void>(`/lawsuit-payments/${id}/`, {
      method: 'DELETE',
    });
  },

  async cancelForwardLawsuitPayment(id: string): Promise<void> {
    return baseApi.request<void>(`/lawsuit-payments/${id}/cancel_forward/`, {
      method: 'POST',
    });
  },

  async updateForwardLawsuitPayment(id: string, data: UpdateForwardPayload): Promise<{ updated: number }> {
    return baseApi.request<{ updated: number }>(`/lawsuit-payments/${id}/update_forward/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};
